import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { NearMe, Waves, People, LocationOn } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  SchoolData,
  DistrictAgg,
  StatsResult,
  NearestNeighborResult,
  CoastalResult,
  CoastalStats,
  DistanceBin,
  LEVEL_COLORS,
  COASTAL_ZONE_COLOR,
  getSchoolLevel,
} from './types';
import { CHART_COLORS, KPI_COLORS, SCORECARD_COLORS, formatNumber } from '../../theme/dashboardTheme';
import SingleValueCard from '../../components/Dashboard/SingleValueCard';

interface AnalysisViewProps {
  filteredSchools: SchoolData[];
  gpsSchools: SchoolData[];
  nearestNeighborMap: Map<string, NearestNeighborResult>;
  coastalDistanceMap: Map<string, CoastalResult>;
  coastalStats: CoastalStats;
  distanceDistribution: DistanceBin[];
  districtAggregates: Map<string, DistrictAgg>;
}

const LEVEL_KEYS = ['Pre-Primary', 'Primary', 'Secondary', 'Basic'];

const truncate = (text: string, max: number) =>
  text.length > max ? text.slice(0, max) + '...' : text;

const AnalysisView: React.FC<AnalysisViewProps> = ({
  filteredSchools,
  gpsSchools,
  nearestNeighborMap,
  coastalDistanceMap,
  coastalStats,
  distanceDistribution,
  districtAggregates,
}) => {
  // ---------- KPI computations from nearestNeighborMap ----------
  const neighborStats = useMemo(() => {
    const entries = Array.from(nearestNeighborMap.values());
    if (entries.length === 0) {
      return {
        avg: 0,
        min: { distanceKm: 0, schoolId: '', nearestSchoolName: '' } as NearestNeighborResult,
        max: { distanceKm: 0, schoolId: '', nearestSchoolName: '' } as NearestNeighborResult,
        minSchoolName: 'N/A',
        maxSchoolName: 'N/A',
      };
    }

    const sum = entries.reduce((acc, e) => acc + e.distanceKm, 0);
    const avg = sum / entries.length;

    let minEntry = entries[0];
    let maxEntry = entries[0];
    for (const entry of entries) {
      if (entry.distanceKm < minEntry.distanceKm) minEntry = entry;
      if (entry.distanceKm > maxEntry.distanceKm) maxEntry = entry;
    }

    // Resolve school names for min/max
    const minSchool = gpsSchools.find((s) => s.school_id === minEntry.schoolId);
    const maxSchool = gpsSchools.find((s) => s.school_id === maxEntry.schoolId);

    return {
      avg,
      min: minEntry,
      max: maxEntry,
      minSchoolName: minSchool?.school_name || 'Unknown',
      maxSchoolName: maxSchool?.school_name || 'Unknown',
    };
  }, [nearestNeighborMap, gpsSchools]);

  // ---------- Coastal percentage ----------
  const coastalPercent = useMemo(() => {
    if (gpsSchools.length === 0) return 0;
    return (coastalStats.schoolCount / gpsSchools.length) * 100;
  }, [coastalStats.schoolCount, gpsSchools.length]);

  // ---------- Row 2 Left: color bins for distance distribution ----------
  const coloredDistribution = useMemo(() => {
    return distanceDistribution.map((bin) => {
      let fill = '#FFC324'; // default yellow
      const rangeStr = bin.range || bin.label || '';
      // Determine color based on range text
      if (rangeStr.includes('0') && !rangeStr.includes('1') && !rangeStr.includes('2')) {
        fill = '#388E3C'; // green for <1km
      } else if (rangeStr.startsWith('0') || rangeStr.startsWith('<') || rangeStr.includes('0.')) {
        fill = '#388E3C';
      }
      // Parse the first number from the range to determine color
      const firstNum = parseFloat(rangeStr.replace(/[^0-9.]/g, ''));
      if (!isNaN(firstNum)) {
        if (firstNum < 1) fill = '#388E3C';
        else if (firstNum >= 1 && firstNum < 5) fill = '#FFC324';
        else fill = '#D32F2F';
      }
      return { ...bin, fill };
    });
  }, [distanceDistribution]);

  // ---------- Row 2 Right: Coastal vs Inland pie ----------
  const coastalPieData = useMemo(() => {
    const coastalCount = coastalStats.schoolCount;
    const inlandCount = gpsSchools.length - coastalCount;
    const coastalEnrollment = coastalStats.totalStudents;
    const inlandEnrollment = gpsSchools.reduce((acc, s) => acc + (s.current_enrollment || 0), 0) - coastalEnrollment;
    return {
      pie: [
        { name: 'Coastal', value: coastalCount, enrollment: coastalEnrollment },
        { name: 'Inland', value: inlandCount, enrollment: inlandEnrollment },
      ],
      colors: [COASTAL_ZONE_COLOR, '#8D6E63'],
    };
  }, [coastalStats, gpsSchools]);

  // ---------- Row 3 Left: Nearest neighbor by district ----------
  const districtNeighborData = useMemo(() => {
    const districtMap = new Map<string, { distances: number[] }>();

    gpsSchools.forEach((school) => {
      const districtName = school.district?.district_name || 'Unknown';
      const nn = nearestNeighborMap.get(school.school_id);
      if (!nn) return;

      if (!districtMap.has(districtName)) {
        districtMap.set(districtName, { distances: [] });
      }
      districtMap.get(districtName)!.distances.push(nn.distanceKm);
    });

    return Array.from(districtMap.entries())
      .map(([name, data]) => {
        const distances = data.distances;
        const avg = distances.reduce((a, b) => a + b, 0) / distances.length;
        const max = Math.max(...distances);
        return {
          district: name,
          avg: parseFloat(avg.toFixed(2)),
          max: parseFloat(max.toFixed(2)),
        };
      })
      .sort((a, b) => b.avg - a.avg);
  }, [gpsSchools, nearestNeighborMap]);

  // ---------- Row 3 Right: Coastal enrollment by level ----------
  const coastalEnrollmentByLevel = useMemo(() => {
    const coastal: Record<string, number> = { 'Pre-Primary': 0, Primary: 0, Secondary: 0, Basic: 0 };
    const inland: Record<string, number> = { 'Pre-Primary': 0, Primary: 0, Secondary: 0, Basic: 0 };

    gpsSchools.forEach((school) => {
      const level = getSchoolLevel(school);
      const coastalResult = coastalDistanceMap.get(school.school_id);
      const isCoastal = coastalResult?.isCoastalZone ?? false;
      const enrollment = school.current_enrollment || 0;

      if (isCoastal) {
        coastal[level] = (coastal[level] || 0) + enrollment;
      } else {
        inland[level] = (inland[level] || 0) + enrollment;
      }
    });

    return [
      {
        zone: 'Coastal',
        'Pre-Primary': coastal['Pre-Primary'],
        Primary: coastal['Primary'],
        Secondary: coastal['Secondary'],
        Basic: coastal['Basic'],
      },
      {
        zone: 'Inland',
        'Pre-Primary': inland['Pre-Primary'],
        Primary: inland['Primary'],
        Secondary: inland['Secondary'],
        Basic: inland['Basic'],
      },
    ];
  }, [gpsSchools, coastalDistanceMap]);

  // ---------- Row 4: Top 20 most isolated schools ----------
  const top20Isolated = useMemo(() => {
    return gpsSchools
      .map((school) => {
        const nn = nearestNeighborMap.get(school.school_id);
        const coastal = coastalDistanceMap.get(school.school_id);
        return {
          school_id: school.school_id,
          school_name: school.school_name,
          district: school.district?.district_name || 'Unknown',
          level: getSchoolLevel(school),
          enrollment: school.current_enrollment || 0,
          distanceKm: nn?.distanceKm ?? null,
          nearestSchoolName: nn?.nearestSchoolName ?? 'N/A',
          coastKm: coastal?.distanceToCoastKm ?? null,
        };
      })
      .filter((s) => s.distanceKm !== null)
      .sort((a, b) => (b.distanceKm as number) - (a.distanceKm as number))
      .slice(0, 20);
  }, [gpsSchools, nearestNeighborMap, coastalDistanceMap]);

  // ---------- Distance color helper ----------
  const getDistanceColor = (km: number | null): string => {
    if (km === null) return '#757575';
    if (km > 5) return '#D32F2F';
    if (km >= 2) return '#FF9800';
    return '#388E3C';
  };

  // ---------- Render ----------
  return (
    <Box sx={{ overflowY: 'auto', height: '100%', p: 2 }}>
      {/* ===== Row 1: KPI Cards ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 1. Avg Nearest Neighbor */}
        <Grid item xs={12} sm={6} md={2}>
          <SingleValueCard
            title="Avg Nearest Neighbor"
            value={neighborStats.avg}
            format="decimal"
            colorScheme="primary"
            icon={<NearMe />}
            unit="km"
          />
        </Grid>

        {/* 2. Min Distance (most clustered) */}
        <Grid item xs={12} sm={6} md={2}>
          <SingleValueCard
            title="Min Distance"
            value={neighborStats.min.distanceKm}
            format="decimal"
            colorScheme="success"
            subtitle={truncate(neighborStats.minSchoolName, 25)}
            unit="km"
          />
        </Grid>

        {/* 3. Max Distance (most isolated) */}
        <Grid item xs={12} sm={6} md={2}>
          <SingleValueCard
            title="Max Distance"
            value={neighborStats.max.distanceKm}
            format="decimal"
            colorScheme="danger"
            subtitle={truncate(neighborStats.maxSchoolName, 25)}
            unit="km"
          />
        </Grid>

        {/* 4. Coastal Schools */}
        <Grid item xs={12} sm={6} md={2}>
          <SingleValueCard
            title="Coastal Schools"
            value={coastalStats.schoolCount}
            format="integer"
            colorScheme="info"
            icon={<Waves />}
            subtitle="within 5km of coast"
          />
        </Grid>

        {/* 5. Coastal Students */}
        <Grid item xs={12} sm={6} md={2}>
          <SingleValueCard
            title="Coastal Students"
            value={coastalStats.totalStudents}
            format="integer"
            colorScheme="primary"
            icon={<People />}
          />
        </Grid>

        {/* 6. Coastal % */}
        <Grid item xs={12} sm={6} md={2}>
          <SingleValueCard
            title="Coastal %"
            value={coastalPercent}
            format="percentage"
            colorScheme="info"
            subtitle="of GPS schools"
          />
        </Grid>
      </Grid>

      {/* ===== Row 2: Distance Distribution + Coastal vs Inland Pie ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Left: Distance Distribution Histogram */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Nearest Neighbor Distance Distribution
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={coloredDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 11 }} label={{ value: 'Schools', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }} />
                  <Tooltip
                    formatter={(value: number) => [`${value} schools`, 'Count']}
                    labelFormatter={(label: string) => `Distance: ${label}`}
                  />
                  <Bar dataKey="count" name="Schools">
                    {coloredDistribution.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Coastal vs Inland Pie */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Coastal vs Inland Schools
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={coastalPieData.pie}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={100}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {coastalPieData.pie.map((entry, idx) => (
                      <Cell key={entry.name} fill={coastalPieData.colors[idx]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string, props: any) => {
                      const enrollment = props.payload?.enrollment ?? 0;
                      return [`${formatNumber(value)} schools (${formatNumber(enrollment)} students)`, name];
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ textAlign: 'center', mt: -1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Coastal: {formatNumber(coastalPieData.pie[0]?.enrollment)} students |
                Inland: {formatNumber(coastalPieData.pie[1]?.enrollment)} students
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ===== Row 3: Nearest Neighbor by District + Coastal Enrollment by Level ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Left: Nearest Neighbor by District */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Avg & Max Nearest School Distance by District
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={districtNeighborData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="district"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tickFormatter={(v: string) => truncate(v, 12)}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    label={{ value: 'Distance (km)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => [`${value.toFixed(2)} km`, name]}
                  />
                  <Legend />
                  <Bar dataKey="avg" name="Avg Distance" fill="#147CD7" />
                  <Bar dataKey="max" name="Max Distance" fill="#E56408" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Coastal Enrollment by Level */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Enrollment by Zone & Level
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={coastalEnrollmentByLevel}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="zone" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    label={{ value: 'Students', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                  />
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                  {LEVEL_KEYS.map((level) => (
                    <Bar
                      key={level}
                      dataKey={level}
                      stackId="levels"
                      fill={LEVEL_COLORS[level]}
                      name={level}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ===== Row 4: Top 20 Most Isolated Schools ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Most Isolated Schools (by Nearest Neighbor Distance)
            </Typography>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>School Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>District</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Level</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Enrollment</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Nearest School (km)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nearest School Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Coast (km)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {top20Isolated.map((row, idx) => (
                    <TableRow key={row.school_id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{row.school_name}</TableCell>
                      <TableCell>{row.district}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.level}
                          size="small"
                          sx={{
                            bgcolor: LEVEL_COLORS[row.level] || LEVEL_COLORS.Unknown,
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(row.enrollment)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          color: getDistanceColor(row.distanceKm),
                        }}
                      >
                        {row.distanceKm !== null ? row.distanceKm.toFixed(2) : 'N/A'}
                      </TableCell>
                      <TableCell>{row.nearestSchoolName}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: 'text.secondary' }}
                      >
                        {row.coastKm !== null ? row.coastKm.toFixed(1) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {top20Isolated.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No proximity data available. Ensure schools have GPS coordinates.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
            >
              Based on {gpsSchools.length} schools with GPS coordinates
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalysisView;
